import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash-es/isEmpty';
import { CheckIcon, CircleHelpIcon, CopyIcon } from 'lucide-react';
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import ToolkitAccordionWrapper from '@/components/organisms/Toolkit/ToolkitAccordionWrapper';
import {
  AutocompleteOption,
  FormAutocomplete,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import useToolkitRule from '@/hooks/useToolkitRule';
import { selectBrainId } from '@/redux/session/selectors';
import { Rule } from '@/shared/types/node';
import { CONDITION_DOCS_URL } from '@/util/constants';
import { addDollarSign, capitalizeFirstLetter } from '@/util/util';
import { LENGTH_L, LENGTH_M } from '@/util/validation/constants';
import { entityOrContext } from '@/util/validation/patterns';

// Max characters before showing tooltip for long names
const MAX_LABEL_LENGTH = 20;

// Schema for toolkit condition rule validation (for categorized combobox)
const createToolkitConditionRuleSchema = (
  t: (key: string, options?: Record<string, unknown>) => string
) =>
  yup.object().shape({
    name: yup
      .string()
      .matches(entityOrContext, { message: () => t('validation.pattern') })
      .min(2)
      .max(LENGTH_L)
      .required(() =>
        t('validation.required_field', {
          0: `${t('common.rule')} ${t('common.name').toLowerCase()}`,
        })
      ),
    value_auto: yup
      .string()
      .max(LENGTH_M)
      .test(
        'conditional-required',
        () =>
          t('validation.required_field', {
            0: `${t('common.rule')} ${t('dialog.value').toLowerCase()}`,
          }),
        function (value) {
          // For $tags, value is not required (user can add new tags)
          if (this.parent.name === '$tags') {
            return true;
          }
          // For other variables, value is required
          return !!value;
        }
      ),
    operator: yup.string(),
  });

type FormType = yup.InferType<ReturnType<typeof createToolkitConditionRuleSchema>>;

interface ToolkitRuleProps {
  rule: Rule;
  onChange: (values: { name?: string; operator?: string; value?: string }, index: number) => void;
  onDelete: (index: number) => void;
  index: number;
  nodeId: string;
  conditionId: string;
  conditionIndex: number;
}

const ToolkitRule = ({
  rule,
  onChange,
  onDelete,
  index,
  nodeId,
  conditionId,
  conditionIndex,
}: ToolkitRuleProps) => {
  const brainId = useSelector(selectBrainId);
  const { getContextVariables } = useDialogs(brainId);
  const variables = getContextVariables();
  const { entityVariables } = useBrainVariables(false);
  const { t } = useTranslation();

  const { hasValue, getDefaultRuleValue, availableValues, operators, enableNewEntry } =
    useToolkitRule(rule);

  // By default show open the first rule
  const [open, setOpen] = useState(index === 0);
  // Track newly created variables
  const [newVariables, setNewVariables] = useState<string[]>([]);
  // Track newly created values (e.g., tags not in the current brain)
  const [newValues, setNewValues] = useState<string[]>([]);

  const onPanelOpen = useCallback(() => {
    setOpen((open) => !open);
  }, []);

  // Create schema with translation function
  const ruleSchema = useMemo(() => createToolkitConditionRuleSchema(t), [t]);

  // RHF Default values
  const defaultValues = useMemo(
    () => ({ name: rule?.name ?? '', operator: rule.operator, value_auto: rule.value ?? '' }),
    [rule.name, rule.operator, rule.value]
  );

  const {
    formState: { errors, isDirty },
    setValue,
    control,
    getValues,
    trigger,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(ruleSchema) as Resolver<FormType>,
  });

  const watchOperator = useWatch({ control, name: 'operator' });
  const watchName = useWatch({ control, name: 'name' });

  // Determine if tags variable is selected (for conditional UI)
  const isTagsVariable = watchName === '$tags';

  // Extract error messages from the form state
  const nameErrorMessage = capitalizeFirstLetter(errors.name?.message);
  const valueErrorMessage = capitalizeFirstLetter(errors.value_auto?.message);

  const onRuleNameChange = useCallback(
    (selectedValue: string) => {
      // Allows user to type a variable name without the dollar sign
      const updatedName = selectedValue.startsWith('@')
        ? selectedValue
        : addDollarSign(selectedValue ?? '');

      // For $tags, we don't want to auto-select a default value - user must explicitly choose
      const shouldAutoSelectValue = updatedName !== '$tags';
      const value_auto = shouldAutoSelectValue ? getDefaultRuleValue(updatedName) : { value: '' };

      const updatedRule = {
        name: updatedName,
        value: value_auto.value,
        operator: operators(updatedName)?.[0]?.value,
      };

      if (selectedValue) {
        // Update the default value only when the user has selected a variable
        // Skip for $tags to allow placeholder to show
        setValue('value_auto', value_auto.value, { shouldValidate: shouldAutoSelectValue });
        setValue('operator', operators(updatedName)?.[0]?.value);
      }

      onChange(updatedRule, index);
    },
    [getDefaultRuleValue, index, onChange, operators, setValue]
  );

  // Handle creating a new variable
  const handleCreateNewVariable = useCallback(
    (variableName: string) => {
      // Add the variable with dollar sign prefix if not already present
      const fullVariableName = variableName.startsWith('@')
        ? variableName
        : addDollarSign(variableName);

      // Add to new variables list
      setNewVariables((prev) => {
        if (!prev.includes(fullVariableName)) {
          return [...prev, fullVariableName];
        }
        return prev;
      });

      // Set it as the selected value
      setValue('name', fullVariableName, { shouldValidate: true });

      // Trigger the onChange callback
      onRuleNameChange(fullVariableName);
    },
    [onRuleNameChange, setValue]
  );

  // Handle creating a new value (e.g., a tag not in the current brain)
  const handleCreateNewValue = useCallback(
    (newValue: string) => {
      // Add to new values list
      setNewValues((prev) => {
        if (!prev.includes(newValue)) {
          return [...prev, newValue];
        }
        return prev;
      });

      // Set it as the selected value
      setValue('value_auto', newValue, { shouldValidate: true });

      // Update redux
      onChange({ value: newValue }, index);
    },
    [index, onChange, setValue]
  );

  const autoCompleteOptions = useMemo(() => {
    const baseOptions = [...variables, ...entityVariables]
      // Filter out the live instructions variable
      .filter((variable) => variable?.value !== '$live_instructions')
      .map((option) => ({
        value: option.value,
        label: option.label,
        category: t(`auto_complete.group_labels.${option.type}`),
      }));

    // Add newly created variables with a custom category
    const newVarOptions = newVariables.map((varName) => ({
      value: varName,
      label: varName,
      category: t('common.new'),
    }));

    return [...baseOptions, ...newVarOptions];
  }, [entityVariables, newVariables, t, variables]);

  useEffect(() => {
    // Opens the first rule when there are errors
    // It is used inside useEffect and not in the
    // useState of open, because rhf errors initially
    // is an empty object
    setOpen(index === 0 || !isEmpty(errors));
  }, [errors, index]);

  const onOperatorChange = useCallback(
    (value: string) => {
      setValue('operator', value);
      const updatedRule: { operator: string; value?: string } = { operator: value };

      const currentOperator = getValues('operator');
      if (currentOperator === 'exist' || currentOperator === 'not_exist') {
        // Clear value when operator is exist or not_exist
        updatedRule['value'] = undefined;
      } else if (!getValues('value_auto') && getValues('name')) {
        const name = getValues('name');
        const defaultValue = getDefaultRuleValue(name);

        updatedRule['value'] = defaultValue.value;

        setValue('value_auto', defaultValue.value, { shouldValidate: true });
      }

      onChange(updatedRule, index);
    },
    [getDefaultRuleValue, getValues, index, onChange, setValue]
  );

  // Convert availableValues to categorized format and include newly created values
  const categorizedAvailableValues = useMemo(() => {
    const baseOptions = availableValues.map((option) => ({
      value: option.value,
      label: option.label,
      category: option.type,
    }));

    // Add newly created values with a custom category
    const newValueOptions = newValues
      .filter((val) => !availableValues.some((opt) => opt.value === val))
      .map((val) => ({ value: val, label: val, category: t('common.new') }));

    const allOptions = [...baseOptions, ...newValueOptions];

    // Move the selected tag to the top of the list
    if (isTagsVariable && rule.value) {
      const selectedValue = rule.value;
      return [
        ...allOptions.filter((o) => o.value === selectedValue),
        ...allOptions.filter((o) => o.value !== selectedValue),
      ];
    }

    return allOptions;
  }, [availableValues, newValues, t, isTagsVariable, rule.value]);

  // Tag-specific props for the value autocomplete
  const tagProps = useMemo(() => {
    const hasTags = availableValues.length > 0;
    return isTagsVariable
      ? {
          placeholder: t(hasTags ? 'common.select_tag' : 'common.add_tag'),
          searchPlaceholder: t(hasTags ? 'common.search_or_type_to_add' : 'common.type_to_add'),
          createNewPlaceholder: t('common.tag'),
          showCreateOnEmpty: true,
        }
      : {
          placeholder: t('common.select_an_option'),
          searchPlaceholder: undefined,
          createNewPlaceholder: undefined,
          showCreateOnEmpty: false,
        };
  }, [isTagsVariable, availableValues.length, t]);

  // Renders dropdown option with copy button on hover and tooltip for long names
  const renderOptionWithCopyButton = useCallback(
    (option: AutocompleteOption, isSelected: boolean) => {
      const handleCopyToClipboard = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        navigator.clipboard.writeText(option.label);
        toast.success(t('common.copied_name_to_clipboard'));
      };

      const isLongName = option.label.length > MAX_LABEL_LENGTH;
      const labelElement = <span className="truncate">{option.label}</span>;

      return (
        <span className="group/option flex flex-1 items-center justify-between gap-2">
          {isLongName ? (
            <InfoTooltip content={option.label} size="auto" delayDuration={300} side="top">
              {labelElement}
            </InfoTooltip>
          ) : (
            labelElement
          )}
          {isSelected && <CheckIcon className="h-4 w-4 shrink-0 group-hover/option:hidden" />}
          <span
            role="button"
            className="hidden h-4 w-4 shrink-0 cursor-pointer text-muted-foreground group-hover/option:block"
            onClickCapture={handleCopyToClipboard}
            onPointerDownCapture={(e) => e.stopPropagation()}
          >
            <CopyIcon className="h-4 w-4" />
          </span>
        </span>
      );
    },
    [t]
  );

  // Renders trigger value with tooltip showing full name for long names (no copy button)
  const renderValueWithTooltip = useCallback((option: AutocompleteOption) => {
    const isLongName = option.label.length > MAX_LABEL_LENGTH;
    const labelElement = <span className="truncate">{option.label}</span>;

    if (isLongName) {
      return (
        <InfoTooltip content={option.label} size="auto" delayDuration={300} side="top">
          {labelElement}
        </InfoTooltip>
      );
    }

    return labelElement;
  }, []);

  const { setFieldError } = useDialogAlert({
    id: conditionId,
    nodeId,
    title: t('actions.types.condition'),
    type: 'condition',
    index: conditionIndex,
  });

  const handleDelete = useCallback(() => {
    // Clear errors
    setFieldError(`name-of-rule-${index}-in-condition-${conditionIndex}`, undefined);
    setFieldError(`value-of-rule-${index}-in-condition-${conditionIndex}`, undefined);

    onDelete(index);
  }, [conditionIndex, index, onDelete, setFieldError]);

  useEffect(() => {
    if (!hasValue(getValues('operator'))) {
      setFieldError(`value-of-rule-${index}-in-condition-${conditionIndex}`, undefined);
    }
  }, [conditionIndex, getValues, hasValue, index, setFieldError]);

  // Clear value error when switching to $tags (value is optional for tags)
  useEffect(() => {
    if (isTagsVariable) {
      setFieldError(`value-of-rule-${index}-in-condition-${conditionIndex}`, undefined);
      trigger('value_auto'); // Re-validate with the conditional schema
    }
  }, [isTagsVariable, conditionIndex, index, setFieldError, trigger]);

  // Dialog errors
  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (isDirty) {
      setFieldError(`name-of-rule-${index}-in-condition-${conditionIndex}`, nameErrorMessage);
    }
  }, [conditionIndex, index, isDirty, nameErrorMessage, setFieldError]);

  useEffect(() => {
    if (isDirty) {
      setFieldError(`value-of-rule-${index}-in-condition-${conditionIndex}`, valueErrorMessage);
    }
  }, [conditionIndex, index, isDirty, setFieldError, valueErrorMessage]);

  const operatorOptions = operators();

  return (
    <div className="mb-2">
      <ToolkitAccordionWrapper
        title={t('dialog.rule_with_number', { 0: index + 1 })}
        subTitle={watchName?.replace(/^\$/, '') ?? ''}
        onChange={onPanelOpen}
        handleDelete={handleDelete}
        expanded={open}
        hideReorderIcon
        disabled={!onDelete}
      >
        <div className="flex flex-col gap-2">
          {/* Check if field */}
          <FormAutocomplete
            name="name"
            control={control}
            options={autoCompleteOptions}
            label={
              <span className="mb-2 flex items-center gap-0.5 text-sm leading-5 font-medium text-foreground">
                {t('dialog.condition.check_if')}
                <InfoTooltip
                  content={t('dialog.condition.check_if_tooltip')}
                  learnMoreUrl={CONDITION_DOCS_URL}
                  side="right"
                >
                  <CircleHelpIcon size={12} className="text-muted-foreground" />
                </InfoTooltip>
              </span>
            }
            placeholder={t('dialog.condition.name_placeholder')}
            onChange={onRuleNameChange}
            onCreateNew={handleCreateNewVariable}
            matchTriggerWidth
            error={!!nameErrorMessage}
            errorMessage={nameErrorMessage}
          />

          {/* Operator select */}
          <Select value={watchOperator} onValueChange={onOperatorChange}>
            <SelectTrigger aria-invalid={!!errors.operator}>
              <SelectValue placeholder={t('dialog.type_operator')} />
            </SelectTrigger>
            <SelectContent>
              {operatorOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.label_key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Value field */}
          {hasValue(watchOperator) && (
            <>
              {availableValues.length > 0 || newValues.length > 0 || enableNewEntry ? (
                <FormAutocomplete
                  name="value_auto"
                  control={control}
                  options={categorizedAvailableValues}
                  onChange={(value) => onChange({ value }, index)}
                  onCreateNew={enableNewEntry ? handleCreateNewValue : undefined}
                  matchTriggerWidth
                  error={!!valueErrorMessage}
                  errorMessage={valueErrorMessage}
                  renderOption={isTagsVariable ? renderOptionWithCopyButton : undefined}
                  renderValue={isTagsVariable ? renderValueWithTooltip : undefined}
                  hideSelectedIndicator={isTagsVariable}
                  {...tagProps}
                />
              ) : (
                <div>
                  <Controller
                    control={control}
                    name="value_auto"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={t('common.type_value')}
                        aria-invalid={!!valueErrorMessage}
                        onChange={(event) => {
                          field.onChange(event);
                          // Updates redux when user types a value
                          onChange({ value: event.target.value }, index);
                        }}
                      />
                    )}
                  />
                  {valueErrorMessage && (
                    <p className="mt-1 text-sm text-destructive">{valueErrorMessage}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </ToolkitAccordionWrapper>
    </div>
  );
};

export default memo(ToolkitRule);
